export const VIEWPORT_SERIES_GEOMETRY_IDLE_DELAY_MS = 1000;
export const VIEWPORT_SERIES_GEOMETRY_INITIAL_GRACE_MS = 15000;
export const VIEWPORT_SERIES_GEOMETRY_DEFER_FRAME_THRESHOLD = 512;

export const shouldDeferSeriesGeometry = frameCount => Number.isSafeInteger(frameCount)
  && frameCount >= VIEWPORT_SERIES_GEOMETRY_DEFER_FRAME_THRESHOLD;

export const createDeferredSeriesGeometryCoordinator = (config = {}) => {
  const {
    setTimer = (callback, delay) => setTimeout(callback, delay),
    clearTimer = timer => clearTimeout(timer),
  } = config;
  const hasLegacyDelay = Object.prototype.hasOwnProperty.call(config, 'delayMs');
  const initialDelayMs = config.initialDelayMs
    ?? (hasLegacyDelay ? config.delayMs : VIEWPORT_SERIES_GEOMETRY_INITIAL_GRACE_MS);
  const interactionDelayMs = config.interactionDelayMs
    ?? (hasLegacyDelay ? config.delayMs : VIEWPORT_SERIES_GEOMETRY_IDLE_DELAY_MS);
  if (!Number.isFinite(initialDelayMs) || initialDelayMs < 0
      || !Number.isFinite(interactionDelayMs) || interactionDelayMs < 0
      || typeof setTimer !== 'function' || typeof clearTimer !== 'function') {
    throw new Error('deferred_geometry_config_invalid');
  }

  let epoch = 0;
  let timer = null;
  let active = null;
  let task = null;

  const clearScheduled = () => {
    if (timer != null) clearTimer(timer);
    timer = null;
  };

  const abortActive = () => {
    active?.controller.abort();
    active = null;
  };

  const schedule = (delayMs) => {
    clearScheduled();
    if (typeof task !== 'function') return false;
    const scheduledEpoch = epoch;
    timer = setTimer(() => {
      timer = null;
      if (scheduledEpoch !== epoch || typeof task !== 'function') return;
      const controller = new AbortController();
      const run = { controller, epoch: scheduledEpoch };
      active = run;
      Promise.resolve().then(() => task({ signal: controller.signal })).then(
        () => {
          if (active === run) active = null;
          if (scheduledEpoch === epoch && !controller.signal.aborted) task = null;
        },
        () => {
          if (active === run) active = null;
          if (scheduledEpoch === epoch && !controller.signal.aborted) task = null;
        },
      );
    }, delayMs);
    return true;
  };

  return Object.freeze({
    replace(nextTask) {
      if (typeof nextTask !== 'function') throw new Error('deferred_geometry_task_invalid');
      epoch += 1;
      clearScheduled();
      abortActive();
      task = nextTask;
      return schedule(initialDelayMs);
    },
    deferForInteraction() {
      if (typeof task !== 'function') return false;
      epoch += 1;
      clearScheduled();
      abortActive();
      return schedule(interactionDelayMs);
    },
    cancel() {
      epoch += 1;
      clearScheduled();
      abortActive();
      task = null;
    },
    snapshot() {
      return Object.freeze({ scheduled: timer != null, active: active != null, pending: typeof task === 'function' });
    },
  });
};
